/**
 * Générateur — Il calcule et interprète l'étendue d'une série (données brutes,
 * tableau, diagramme en bâtons, diagramme circulaire, histogramme).
 * Attendu ID : 214 | Niveau : 3ème | Domaine : Données (d14, O2)
 *
 * Source doc prof (OGD FONCTIONS 3e, séance B2S3). Plan de cartes auto-validé
 * 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « tableau »        — le triptyque total/moyenne/médiane/étendue (tableau)
 *   « batons »         — diagramme en bâtons (svgBarres 5e) : lecture + indicateurs
 *   « brutes »         — données brutes : médiane, étendue
 *   « deduction »      — min = max − étendue (les finales du 100 m)
 *   « critere »        — double critère moyenne ET étendue (label qualité, QCM)
 *                        [v2 2026-07-26, campagne famille C : le critère « plus de
 *                        la moitié > 17 cm » (proportion, hors attendu) est remplacé
 *                        par une étendue réellement calculée et interprétée, et le
 *                        SUPPORT varie : tableau, bâtons, circulaire, histogramme]
 *   « interpretation » — que signifie l'étendue ? (QCM à valeur calculée)
 *
 * Moyennes EXACTES par construction (somme divisible), arrondis PARTAGÉS,
 * médianes ENTIÈRES (rangs contrôlés).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { imgElement, svgBarres, svgCamembert, tableEnonce, ligneTable } from '../../5eme/statistiques/svgStats';
import { somme, medianeTableau, svgHisto } from './donnees';

const ATTENDU_ID = 214;

const L_TABLEAU = 'Calculer moyenne, médiane et étendue (tableau)';
const L_BATONS = 'Lire un diagramme en bâtons et calculer les indicateurs';
const L_BRUTES = 'Médiane et étendue de données brutes';
const L_DEDUCTION = 'Retrouver le minimum grâce à l\'étendue';
const L_CRITERE = 'Vérifier un double critère (moyenne et étendue)';
const L_INTERPRETATION = 'Interpréter l\'étendue';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const MOINS = '−';   // U+2212, affichage seulement

// Série (valeurs consécutives ou espacées, effectifs) à MÉDIANE ENTIÈRE.
const tirerSerieTableau = () => {
    for (let essai = 0; essai < 120; essai++) {
        const debut = randInt(5, 8);
        const nb = randInt(6, 8);
        const valeurs = Array.from({ length: nb }, (_, i) => debut + i);
        const effectifs = valeurs.map(() => randInt(2, 12));
        const med = medianeTableau(valeurs, effectifs);
        if (Number.isInteger(med)) return { valeurs, effectifs, med };
    }
    return { valeurs: [6, 7, 8, 9, 10, 11], effectifs: [4, 6, 10, 11, 14, 9], med: 9 };
};

const tableEff = (valeurs, effectifs, uniteVal) => tableEnonce(
    ligneTable(uniteVal, valeurs.map(String)),
    ligneTable('Effectif', effectifs.map(String)),
);

// Arrondi au dixième PARTAGÉ gen↔solveur, garde anti-pivot + décimale ≠ 0.
const moyenneDixieme = (sommeTot, N) => {
    const m10x = (sommeTot * 10) / N;
    const arrondi = Math.round(m10x);
    const frac = m10x - Math.floor(m10x);
    if (Math.abs(frac - 0.5) < 0.05 || arrondi % 10 === 0) return null;   // pivot ou ,0
    return `${Math.floor(arrondi / 10)},${arrondi % 10}`;
};

// ═════════ « tableau » — le triptyque ═════════

const genTableauTrous = () => {
    let serie; let moy;
    for (let essai = 0; essai < 120; essai++) {
        serie = tirerSerieTableau();
        const N = somme(serie.effectifs);
        const S = serie.valeurs.reduce((acc, v, i) => acc + v * serie.effectifs[i], 0);
        moy = moyenneDixieme(S, N);
        if (moy) break;
        moy = null;
    }
    if (!moy) { serie = { valeurs: [6, 7, 8, 9, 10, 11], effectifs: [5, 6, 10, 11, 14, 15], med: 9 }; moy = '9,1'; } // repli VÉRIFIÉ : 556/61 ≈ 9,1 ; 31e valeur = 9
    const N = somme(serie.effectifs);
    const etendue = serie.valeurs[serie.valeurs.length - 1] - serie.valeurs[0];
    const phrase = `$\\text{Effectif total : } {{${N}}} \\qquad \\text{moyenne} \\approx {{${moy}}}`
        + ` \\qquad \\text{médiane : } {{${serie.med}}} \\qquad \\text{étendue : } {{${etendue}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p>Les notes obtenues à un contrôle commun :</p>'
        + tableEff(serie.valeurs, serie.effectifs, 'Note')
        + '<p><b>Calcule l\'effectif total, la moyenne, la médiane et l\'étendue.</b> '
        + 'Arrondis la moyenne au dixième.</p>',
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = { ...serie, N, moy, etendue };
    return payload;
};

// ═════════ « batons » — diagramme en bâtons ═════════

const genBatonsTrous = () => {
    let masses; let eff; let med;
    for (let essai = 0; essai < 120; essai++) {
        masses = [1, 2, 3, 4, 5, 6, 7];
        eff = masses.map(() => randInt(1, 7));
        med = medianeTableau(masses, eff);
        if (Number.isInteger(med) && somme(eff) >= 15) break;
        med = null;
    }
    if (!med) { masses = [1, 2, 3, 4, 5, 6, 7]; eff = [3, 5, 6, 5, 2, 2, 1]; med = 3; }
    const N = somme(eff);
    const auMoins = randInt(3, 4);
    const nbAuMoins = somme(eff.slice(auMoins - 1));
    const etendue = 7 - 1;
    const phrase = `Nombre total de colis : {{${N}}} ; colis d'au moins ${auMoins} kg : {{${nbAuMoins}}} ; masse médiane : {{${med}}} kg ; étendue : {{${etendue}}} kg.`;
    const payload = makeTexteTrou(phrase,
        '<p>Le diagramme donne les masses des colis expédiés dans une journée.</p>'
        + '<p><b>Lis le diagramme puis complète.</b></p>',
        { ...opts(L_BATONS), mode: 'saisie' });
    // svgBarres (5e) n'émet pas de <desc> → on l'injecte (figure auto-descriptive).
    const svg = svgBarres(masses.map(m => `${m} kg`), eff, { yStep: 1, batons: true, yLabel: 'Nombre de colis' })
        .replace(/(<svg[^>]*>)/, `$1<desc>batons|${eff.join(',')}</desc>`);
    payload.elements_additionnels = imgElement(svg);
    payload._meta.params = { eff, N, auMoins, nbAuMoins, med };
    return payload;
};

// ═════════ « brutes » — données brutes ═════════

const CONTEXTES_BRUTS = [
    ['les distances, en mètres, des lancers de javelot d\'un groupe', 'm'],
    ['les tailles, en centimètres, des plants de tomates d\'une serre', 'cm'],
    ['les temps, en minutes, mis par des élèves pour venir au collège', 'min'],
];

const genBrutesTrous = () => {
    const [sujet, unite] = randomChoice(CONTEXTES_BRUTS);
    const n = randomChoice([11, 13, 15]);                 // IMPAIR → médiane = valeur centrale
    const base = randInt(20, 40);
    const valeurs = Array.from({ length: n }, () => base + randInt(0, 16));
    const tri = [...valeurs].sort((a, b) => a - b);
    const med = tri[(n - 1) / 2];
    const etendue = tri[n - 1] - tri[0];
    const phrase = `Médiane : {{${med}}} ${unite} ; étendue : {{${etendue}}} ${unite}.`;
    const payload = makeTexteTrou(phrase,
        `<p>On a relevé ${sujet} :</p><p><b>${valeurs.join(' ; ')}</b></p>`
        // « la Ne valeur après avoir trié » donnait le rang ET la démarche.
        + '<p><b>Détermine la médiane et l\'étendue.</b></p>',
        { ...opts(L_BRUTES), mode: 'saisie' });
    payload._meta.params = { sujet, valeurs, med, etendue };
    return payload;
};

// ═════════ « deduction » — min = max − étendue ═════════

const genDeductionTrous = () => {
    // Série A donnée (8 temps au centième) ; série B décrite par max + étendue.
    // Les deux RÉPONSES (minA, minB) ne finissent jamais par un centième 0 :
    // « 9,50 » piégerait l'élève qui tape « 9,5 » (convention sans zéro final).
    let tempsA; let minA; let maxB; let etB; let minB;
    for (let essai = 0; essai < 120; essai++) {
        const baseA = 975 + randInt(0, 15);                // en centièmes de s
        tempsA = Array.from({ length: 8 }, () => baseA + randInt(0, 30));
        minA = Math.min(...tempsA);
        maxB = 1150 + randInt(0, 60);                      // en centièmes
        etB = randInt(180, 260);
        minB = maxB - etB;
        if (new Set(tempsA).size === 8 && minA % 10 !== 0 && minB % 10 !== 0) break;
    }
    const fr = (c) => `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`;
    const phrase = `Meilleur temps 2016 : {{${fr(minA)}}} s ; meilleur temps 2012 : {{${fr(minB)}}} s.`;
    const payload = makeTexteTrou(phrase,
        `<p>Finale du 100 m, JO 2016 — les huit temps, en secondes : ${tempsA.map(fr).join(' ; ')}.</p>`
        + `<p>Finale des JO 2012 : le temps le plus LONG vaut ${fr(maxB)} s et l'étendue vaut ${fr(etB)} s.</p>`
        // « l'étendue permet de retrouver le minimum ! » était le raisonnement
        // à trouver pour la seconde finale.
        + '<p><b>Donne le meilleur temps de chaque finale.</b></p>',
        { ...opts(L_DEDUCTION), mode: 'saisie' });
    payload._meta.params = { tempsA, minA, maxB, etB, minB };
    return payload;
};

// ═════════ « critere » — double critère moyenne ET étendue (QCM, v2) ═════════
// v2 : l'ancien critère « plus de la moitié > 17 cm » (proportion, hors attendu)
// disparaît ; l'étendue est réellement calculée (extrêmes VARIABLES) et
// interprétée (homogénéité). Le SUPPORT varie comme l'exige le libellé de 214 :
// tableau, diagramme en bâtons, diagramme circulaire, histogramme (figures
// auto-descriptives via <desc>, patron des chantiers 4e).

const CH_OUI = 'Oui : les deux critères sont vérifiés.';
const CH_NON_ET = 'Non : la moyenne convient, mais la récolte est trop hétérogène (étendue trop grande).';
const CH_NON_MOY = 'Non : l\'étendue convient (récolte homogène), mais la moyenne est trop faible.';

const injecteDesc = (svg, desc) => svg.replace(/(<svg[^>]*>)/, `$1<desc>${desc}</desc>`);

const genCritereQcm = () => {
    let support = randomChoice(['tableau', 'batons', 'circulaire', 'histo']);
    const seuilMoy = randomChoice([16, 17]);               // moyenne exigée (« ≥ seuilMoy cm »)
    let valeurs; let eff; let moyOk; let etOk; let seuilEt; let etendue; let N; let S; let lo0; let nb;
    for (let essai = 0; essai < 120; essai++) {
        if (support === 'histo') {
            // Classes d'amplitude 2 cm : moyenne via les CENTRES (convention
            // énoncée), étendue entre les BORNES extrêmes = 2·nb classes.
            // Consts locales à l'itération (no-loop-func) puis recopie dans les
            // variables de sortie — comportement identique.
            const lo = randomChoice([11, 12, 13]);
            const n = randomChoice([4, 5, 6]);
            lo0 = lo;
            nb = n;
            eff = Array.from({ length: n }, () => randInt(4, 12));   // en centaines
            valeurs = Array.from({ length: n }, (_, i) => lo + 2 * i + 1);   // centres
            etendue = 2 * nb;                              // bornes : (lo0 + 2nb) − lo0
            seuilEt = randomChoice([9, 10, 11]);
        } else {
            const vmin = randomChoice([12, 13, 14]);
            const vmax = randomChoice([21, 22, 23]);
            valeurs = [vmin, 15, 17, 19, vmax];
            eff = valeurs.map(() => randInt(4, 12));       // en centaines (lisible sur les figures)
            etendue = vmax - vmin;                         // ∈ [7 ; 11], réellement calculée
            seuilEt = randomChoice([8, 9, 10]);
        }
        N = somme(eff);
        S = valeurs.reduce((acc, v, i) => acc + v * eff[i], 0);
        moyOk = S >= seuilMoy * N;                         // comparaison exacte, pas de division
        etOk = etendue <= seuilEt;
        const cas = (moyOk ? 1 : 0) + (etOk ? 2 : 0);
        if (cas === 3 || cas === 1 || cas === 2) break;    // les 3 issues utiles (jamais « double non »)
        moyOk = null;
    }
    if (moyOk === null) { support = 'tableau'; valeurs = [12, 15, 17, 19, 23]; eff = [4, 7, 5, 15, 4]; N = 35; S = 615; etendue = 11; seuilEt = 10; moyOk = true; etOk = false; } // repli (servi en tableau) VÉRIFIÉ : 615/35 ≈ 17,6 ≥ 16 et 17 ; étendue 23 − 12 = 11 > 10

    const criteres = `<p>Label de qualité si : moyenne ≥ ${seuilMoy} cm ET étendue ≤ ${seuilEt} cm (récolte homogène).</p>`
        + '<p><b>Calcule l\'étendue et la moyenne : le cultivateur obtient-il le label ?</b></p>';
    let texte; let figure = null;
    if (support === 'tableau') {
        texte = '<p>Longueurs des gousses d\'une récolte de vanille — effectifs en centaines :</p>'
            + tableEff(valeurs, eff, 'Longueur (cm)') + criteres;
    } else if (support === 'batons') {
        texte = `<p>Le diagramme en bâtons donne les longueurs des gousses d'une récolte de vanille — effectifs en centaines, ${N} centaines de gousses en tout.</p>` + criteres;
        figure = injecteDesc(
            svgBarres(valeurs.map(String), eff, { yStep: 1, yLabel: 'Effectif (centaines)', xLabel: 'Longueur (cm)' }),
            `critere|batons|${valeurs.join(',')}|${eff.join(',')}`,
        );
    } else if (support === 'circulaire') {
        texte = `<p>Le diagramme circulaire décrit les longueurs des gousses d'une récolte de vanille : la légende donne l'effectif de chaque longueur, en centaines — ${N} centaines de gousses en tout.</p>` + criteres;
        figure = injecteDesc(
            svgCamembert(
                valeurs.map((v, i) => ({ nom: `${v} cm`, pct: (eff[i] * 100) / N, etiquette: `${eff[i]}` })),
                { titre: 'Récolte de vanille', sousTitre: `${N} centaines de gousses` },
            ),
            `critere|circulaire|${valeurs.join(',')}|${eff.join(',')}`,
        );
    } else {
        texte = `<p>L'histogramme donne les longueurs des gousses d'une récolte de vanille, regroupées en classes de 2 cm — effectifs en centaines, ${N} centaines de gousses en tout.</p>`
            + '<p>Pour la moyenne, on assimile chaque gousse au CENTRE de sa classe ; l\'étendue se lit entre les bornes extrêmes de l\'histogramme.</p>' + criteres;
        figure = svgHisto(lo0, 2, eff, { yStep: 1, yLabel: 'Effectif (centaines)', xLabel: 'Longueur (cm)' });
    }

    const correct = moyOk && etOk ? CH_OUI : (moyOk ? CH_NON_ET : CH_NON_MOY);
    const payload = makeQcm([CH_OUI, CH_NON_ET, CH_NON_MOY], correct, texte, opts(L_CRITERE));
    if (figure) payload.elements_additionnels = imgElement(figure);
    payload._meta.params = { support: support, valeurs, eff, N, seuilMoy, seuilEt, etendue, moyOk, etOk, lo0, nb };
    return payload;
};

// ═════════ « interpretation » — que signifie l'étendue ? (QCM) ═════════

const genInterpretationQcm = () => {
    const [sujet, unite] = randomChoice(CONTEXTES_BRUTS);
    const minV = randInt(10, 30); const et = randInt(8, 25);
    const maxV = minV + et;
    const correct = `Les valeurs s'étalent sur ${et} ${unite} : l'écart entre la plus grande et la plus petite vaut ${et} ${unite}.`;
    const choix = [correct,
        `La valeur la plus fréquente de la série est ${et} ${unite}.`,
        `La moitié des valeurs dépasse ${et} ${unite}.`];
    const payload = makeQcm(choix, correct,
        `<p>On a relevé ${sujet}. La plus petite valeur est ${minV} ${unite}, la plus grande ${maxV} ${unite} ; `
        + `l'étendue vaut donc ${maxV} ${MOINS} ${minV} = ${et} ${unite}.</p>`
        + '<p><b>Que signifie cette étendue ?</b></p>',
        opts(L_INTERPRETATION));
    payload._meta.params = { sujet, minV, maxV, et };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    tableau_texte_trou_saisie: genTableauTrous,
    batons_texte_trou_saisie: genBatonsTrous,
    brutes_texte_trou_saisie: genBrutesTrous,
    deduction_texte_trou_saisie: genDeductionTrous,
    critere_qcm_unique: genCritereQcm,
    interpretation_qcm_unique: genInterpretationQcm,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'batons', label: L_BATONS, formatsList: buildFormats('batons') },
    { id: 'brutes', label: L_BRUTES, formatsList: buildFormats('brutes') },
    { id: 'deduction', label: L_DEDUCTION, formatsList: buildFormats('deduction') },
    { id: 'critere', label: L_CRITERE, formatsList: buildFormats('critere') },
    { id: 'interpretation', label: L_INTERPRETATION, formatsList: buildFormats('interpretation') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
